import * as React from "react";
import classNames from "classnames";
import { Icon, Intent, Tooltip, Position } from "@blueprintjs/core";
import { observer } from "mobx-react";
import copyToClipboard from "copy-to-clipboard";
import { AppToaster } from "../toaster";
import { logEvent } from "../../utils/analytics";
import { GenomicVariant } from "../../api";
import { useContext } from "react";
import { StoresContext } from "../../pages/_app";

interface Props {
  variant: GenomicVariant;
  selectVariant: (variant: GenomicVariant) => void;
}

export const ViralAnnotationTableRow = observer((props: Props) => {
  const rootStore = useContext(StoresContext);
  const { viralVariantStore } = rootStore;
  const { variant } = props;

  function selectVariant() {
    props.selectVariant(variant);
  }

  function truncate(s: string) {
    return s.length >= 10 ? s.slice(0, 7) + "..." : s;
  }

  function copySequence(kind: "reference" | "alternate") {
    const sequence = kind === "reference" ? props.variant.reference : props.variant.alternate;
    // eslint-disable-next-line
    AppToaster!.show({
      message: "Nucleotide sequence copied",
      intent: Intent.PRIMARY,
    });
    logEvent("viral variant", "Copy NT sequence", props.variant.uuid);
    copyToClipboard(sequence);
  }

  const selected = variant.uuid === viralVariantStore.selectedUuid;
  const segmentName = variant.segment_name === "unsegmented" ? "–" : variant.segment_name;
  const geneName = variant.gene_name || "–";
  const geneProduct = variant.product || "–";
  const geneType = variant.feature_type || "–";
  const position = variant.position + 1;
  // TODO: the variant reference value in the bootstrap isn't used when variant_type is insertion
  // probably remove this once bootstrap updated
  const variantType = variant.variant_type || "–";
  const formattedReference = variantType === "Insertion" ? "–" : variant.reference;
  const formattedAlternate = variantType === "Deletion" ? "–" : variant.alternate;
  const reference = truncate(formattedReference);
  const alternate = truncate(formattedAlternate);
  const classes = classNames(["annotation-row", "viral-row"], {
    "annotation-row--selected": selected,
  });

  const renderedReference =
    variant.reference.length > 10 ? (
      <td style={{ cursor: "pointer" }} onClick={() => copySequence("reference")}>
        <Tooltip content="Click to copy reference nucleotide sequence" position={Position.TOP}>
          {reference}
        </Tooltip>
      </td>
    ) : (
      <td>{reference}</td>
    );

  const renderedAlternate =
    variant.alternate.length > 10 && !(variantType === "Deletion") ? (
      <td style={{ cursor: "pointer" }} onClick={() => copySequence("alternate")}>
        <Tooltip content="Click to copy alternate nucleotide sequence" position={Position.TOP}>
          {alternate}
        </Tooltip>
      </td>
    ) : (
      <td>{alternate}</td>
    );

  return (
    <>
      <tr className={classes}>
        <td>{segmentName}</td>
        <td>{position}</td>
        <td>{variantType}</td>
        {renderedReference}
        {renderedAlternate}
        <td>
          <i>{geneName}</i>
        </td>
        <td>
          <div className="gene-product-cell">{geneProduct}</div>
        </td>
        <td>{geneType}</td>
        {viralVariantStore.canScroll && (
          <td onClick={() => selectVariant()}>
            <Icon className="link-icon" icon="geolocation" />
          </td>
        )}
      </tr>
    </>
  );
});
